import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { NextRouter, useRouter } from 'next/router';
import { RPCSchemas } from '@bits/rpc';
import {
  AdvancedFilterGroup,
  AdvancedFilterObject,
  SavedAdvancedFilter,
  useToast,
} from '@bits/ui';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import {
  facetAdvancedFilterOptionTransformMap,
  FilterFacetOptionTransform,
} from '@/lib/advanced-filters/facet-type-handlers';
import {
  saveAdvancedFilters,
  useSavedAdvancedFilters,
} from '@/lib/advanced-filters/use-saved-filters';
import { usePopulateCustomPropertiesTranslationsAndTypes } from '@/lib/custom-properties/custom-properties-translations';

type ListSorting = {
  field: string;
  order: 'ASCENDING' | 'DESCENDING';
};

/**
 * Used in conjunction with the controlled component
 * AdvancedFilters to display and filter a list of items
 * using the normalized filtered OpenSearch API endpoints
 */
export const useFilteredList = ({
  savedFiltersKey,
  defaultFilters,
  filterValueTransforms,
  fetchFacets,
  facetOptionTransforms,
}: {
  savedFiltersKey: string;
  defaultFilters?: AdvancedFilterObject;
  filterValueTransforms?: Record<string, (value: string) => React.ReactNode>;
  fetchFacets: () => Promise<{ facets: RPCSchemas['SearchFacet'][] }>;
  facetOptionTransforms?: FilterFacetOptionTransform;
}) => {
  const { toast } = useToast();
  const { data: { facets } = {}, isLoading: isLoadingFacets } = useSWR<{
    facets: RPCSchemas['SearchFacet'][];
  }>([savedFiltersKey, 'facets'], fetchFacets, {
    onError: () => {
      toast({
        variant: 'error',
        title: t('errors:item-list.title'),
        description:
          'There was an error fetching the list results. Please try again later.',
      });
    },
  });

  const router = useRouter();
  /**
   * The page can be set both by the router, and programatically
   * by setting the page state.
   *
   * It listens to the router state, but can set the router state by
   * being set.
   */
  const routerPage = router.query['page'] ?? 1;
  const [page, setPage] = useState<number>(routerPage ? +routerPage : 1);

  const sort = router.query.sort?.toString() || 'createdAt:DESCENDING';
  const search = router.query.search?.toString();
  const [mounted, setMounted] = useState(false);
  const { t } = useTranslation();

  const { data: savedFilters } = useSavedAdvancedFilters(savedFiltersKey);

  const [activeFilters, setActiveFilters] = useState<AdvancedFilterObject>({});
  const [activeSavedFilterId, setActiveSavedFilterId] = useState<
    string | undefined
  >(undefined);

  const activeSavedFilter = useMemo(
    () => savedFilters?.find((f) => f.id === activeSavedFilterId),
    [savedFilters, activeSavedFilterId]
  );

  const setFilterState = useCallback(
    (filters: AdvancedFilterObject, savedFilterId?: string) => {
      setActiveFilters(filters);
      setActiveSavedFilterId(savedFilterId);

      if (savedFiltersKey) {
        setLocalStorageFilters(savedFiltersKey, filters, savedFilterId);
      }
    },
    [savedFiltersKey]
  );

  const didMount = useRef(false);
  useEffect(() => {
    if (didMount.current || !facets) {
      return;
    }

    setMounted(true);
    didMount.current = true;

    const routerQuery = Object.fromEntries(
      new URLSearchParams(location.search).entries()
    );

    const urlContainsFilters = Object.keys(routerQuery).some((key) =>
      facets.some((f) => f.name === key)
    );
    const urlContainsSavedFilter = !!routerQuery.savedFilter;

    // If the URL contains filters or a saved filter, set the active filters
    if (urlContainsFilters || urlContainsSavedFilter) {
      const filters = facets.reduce(
        (acc, facet) => {
          if (routerQuery[facet.name]) {
            acc[facet.name] = (routerQuery[facet.name] as string).split(',');
          }
          return acc;
        },
        {} as Record<string, string[]>
      );

      setFilterState(filters, routerQuery.savedFilter || undefined);
      return;
    }

    // Otherwise, use the filters from localStorage
    if (savedFiltersKey) {
      const localStorageFilters = getLocalStorageFilters(savedFiltersKey);

      if (localStorageFilters) {
        setFilterState(
          localStorageFilters.filters,
          localStorageFilters.savedFilterId
        );

        return;
      }
    }

    // Otherwise, use the default filters
    if (defaultFilters) {
      setFilterState(defaultFilters, undefined);
    }
  }, [isLoadingFacets]);

  const filterOptions: AdvancedFilterGroup[] | null = useMemo(() => {
    return facets
      ? facets
          .map((facet): AdvancedFilterGroup | undefined =>
            facetAdvancedFilterOptionTransformMap[facet.type](
              facet,
              t,
              filterValueTransforms,
              facetOptionTransforms
            )
          )
          .filter((f): f is AdvancedFilterGroup => f !== undefined)
      : null;
  }, [isLoadingFacets, t]);

  usePopulateCustomPropertiesTranslationsAndTypes();

  // When filters change, update the router to reflect the new filters
  useEffect(() => {
    if (!filterOptions) {
      return;
    }

    updateRouterQueryWithFilters(
      router,
      filterOptions,
      activeFilters,
      activeSavedFilterId,
      page
    );
  }, [filterOptions, activeFilters, activeSavedFilterId, page]);

  useEffect(() => {
    setPage(router.query.page ? +router.query.page : 1);
  }, [router.query.page]);

  /**
   * Active filters for the request
   * We filter out empty value arrays to avoid sending empty filters to the API
   */
  const activeRequestFilters = {
    // Add the saved filter options
    ...Object.entries(activeSavedFilter?.filters || {}).reduce(
      (acc, [key, value]) => {
        if (value.length > 0) {
          acc[key] = value.toString().split(',');
        }
        return acc;
      },
      {} as Record<string, string[]>
    ),
    ...Object.fromEntries(
      Object.entries(activeFilters).filter(([_, values]) => values.length > 0)
    ),
  };

  /**
   * The formatted filters that are used for the actual request to the API
   */
  const formattedRequestFilters = useMemo<RPCSchemas['SearchFilter'][] | null>(
    () =>
      facets
        ? (Object.entries(activeRequestFilters)
            .map(([field, values]) => {
              const facet = facets.find((f) => f.name === field);

              if (facet?.type === 'VALUE') {
                return {
                  type: 'VALUE',
                  origin: facet.origin,
                  field,
                  values: values.map((v) => String(v)),
                };
              }

              if (facet?.type === 'BOOLEAN') {
                return facet.values?.length === values.length
                  ? null
                  : {
                      type: 'BOOLEAN',
                      origin: facet.origin,
                      field,
                      // If both values of a boolean facet are selected, they cancel each other out, so we return empty
                      value: values[0] === 'true' ? true : false,
                    };
              }

              if (facet?.type === 'DATE_RANGE') {
                return {
                  type: 'DATE_RANGE',
                  origin: facet.origin,
                  field,
                  values: [
                    {
                      from: values[0] ? safeDateToISOString(values[0]) : null,
                      to: values[1] ? safeDateToISOString(values[1]) : null,
                    },
                  ],
                };
              }

              return null;
            })
            .filter((f) => f !== null) as RPCSchemas['SearchFilter'][])
        : null,
    [activeRequestFilters, facets]
  );

  const onSearchChange = useMemo(
    () => (value: string) => {
      router.replace({
        query: {
          ...router.query,
          search: value,
          page: 1,
        },
      });
    },
    [router]
  );

  const sortBy = useMemo<ListSorting>(() => {
    const [field, order] = sort.split(':');
    return {
      field,
      order,
    } as ListSorting;
  }, [sort]);

  const onSortChange = useCallback(
    (field: string) => {
      router.replace({
        query: {
          ...router.query,
          sort:
            field +
            ':' +
            (sortBy.field === field
              ? sortBy.order === 'ASCENDING'
                ? 'DESCENDING'
                : 'ASCENDING'
              : 'DESCENDING'),
        },
      });
    },
    [router, sortBy]
  );

  const setSavedFilters = useCallback(
    async (filters: SavedAdvancedFilter[]) => {
      if (savedFiltersKey) {
        await saveAdvancedFilters(savedFiltersKey, filters);
      }
    },
    [savedFiltersKey, activeRequestFilters]
  );

  const handleSetFilters = useCallback(
    (filters: AdvancedFilterObject) => {
      setPage(1);
      setFilterState(filters, activeSavedFilterId);
    },
    [activeSavedFilterId]
  );

  const ready = mounted && !!formattedRequestFilters;

  return {
    filterOptions,

    setFilters: handleSetFilters,
    activeFilters,
    formattedRequestFilters,

    onSearchChange,
    onSortChange,
    sortBy,
    page,
    search,

    ready,

    savedFilters,
    setActiveSavedFilter: (savedFilterId?: string) =>
      // When setting the active saved filter, we want to clear the active filters
      setFilterState({}, savedFilterId),
    activeSavedFilter: activeSavedFilter?.id,
    setSavedFilters,
  };
};

const updateRouterQueryWithFilters = (
  router: NextRouter,
  filterOptions: AdvancedFilterGroup[],
  filters: AdvancedFilterObject,
  savedFilterId?: string,
  page?: number
) => {
  const newQuery = {
    ...router.query,
  };

  filterOptions.forEach((filter) => {
    delete newQuery[filter.id];
  });

  if (savedFilterId) {
    newQuery.savedFilter = savedFilterId;
  } else {
    delete newQuery.savedFilter;
  }

  const routerQuery = {
    ...newQuery,
    ...(page ? { page: page.toString() } : {}),
    ...Object.entries(filters).reduce(
      (acc, [key, values]) => ({
        ...acc,
        [key]: values
          .map((value) =>
            filterOptions.find((f) => f.id === key)?.type === 'date-range'
              ? value
                ? new Date(value).toISOString()
                : undefined
              : value?.toString()
          )
          .join(','),
      }),
      {}
    ),
  };

  router.replace(
    {
      query: routerQuery,
    },
    undefined,
    {
      shallow: true,
    }
  );
};

const setLocalStorageFilters = (
  savedFiltersKey: string,
  filters: AdvancedFilterObject,
  savedFilterId?: string
) => {
  localStorage.setItem(
    'filter-state-' + savedFiltersKey,
    JSON.stringify({
      filters,
      savedFilterId,
    })
  );
};

const getLocalStorageFilters = (savedFiltersKey: string) => {
  const savedFilters = localStorage.getItem('filter-state-' + savedFiltersKey);

  if (!savedFilters) {
    return null;
  }

  return JSON.parse(savedFilters) as {
    filters: AdvancedFilterObject;
    savedFilterId: string;
  };
};

const safeDateToISOString = (dateValue: string | Date): string | null => {
  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString();
};
